import { Box, Grid, Stack } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { MemberSection, PageTitle } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { countryName } from '~/lib/country'
import { MemberSectionGuard } from './MemberGuard'

/** `/me/profile`: "My information". Label over value on a phone, side by side from `sm`; a long email wraps. */
export default function Profile() {
  const { t } = useT()
  return (
    <MemberSectionGuard>
      {({ member }) => (
        // The page is one section: a raised card from `md`, no frame on a phone (as Write to us and Password).
        <MemberSection frameFrom='md' maxW='3xl'>
          <Stack gap={4}>
            <PageTitle title={t('dashboard.info')} />
            <Grid
              as='dl'
              maxW='3xl'
              templateColumns={{ base: 'minmax(0, 1fr)', sm: '12rem minmax(0, 1fr)' }}
              columnGap={6}
              rowGap={{ base: 1, sm: 4 }}
              m={0}
            >
              <Row label={t('dashboard.name')}>{member.name}</Row>
              <Row label={t('dashboard.email')}>{member.email}</Row>
              {member.city && (
                <Row label={t('dashboard.city')}>
                  {[member.city.name, member.city.admin1, countryName(member.city.country)].filter(Boolean).join(', ')}
                </Row>
              )}
              <Row label={t('dashboard.since')}>
                <time dateTime={member.registeredAt}>
                  {new Date(member.registeredAt).toLocaleDateString(undefined, { dateStyle: 'long' })}
                </time>
              </Row>
              <Row label={t('dashboard.checkedBy')}>{member.provider}</Row>
            </Grid>
          </Stack>
        </MemberSection>
      )}
    </MemberSectionGuard>
  )
}

/** One label/value pair: stacked on a phone (label muted above), side by side from `sm`; long values wrap. */
const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <Box as='dt' color='fg.muted' mt={{ base: 3, sm: 0 }}>
      {label}
    </Box>
    <Box as='dd' m={0} minW={0} overflowWrap='anywhere'>
      {children}
    </Box>
  </>
)
